// src/components/Dashboard/AdminDashboard/AllUsers/AdminRoleReportReview.js

import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../../../../config';
import { apiFetch } from '../../../../apiFetch';
import './AdminRoleReportReview.css';

const AdminRoleReportReview = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [roleFilter, setRoleFilter] = useState('all'); // all | agent | scout | club

    useEffect(() => {
        const fetchReports = async () => {
            try {
                const response = await apiFetch('/api/admin/role-reports');
                if (!response.ok) {
                    throw new Error(await response.text());
                }

                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                    throw new Error('Received non-JSON response');
                }

                const data = await response.json();
                setReports(data || []);
            } catch (err) {
                console.error('❌ Failed to fetch unified reports:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchReports();
    }, []);

    const getFrontendLink = (raw) => {
        if (!raw) return null;
        if (/^https?:\/\//i.test(raw)) return raw;
        return `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
    };

    const getFileUrl = (rawUrl) => {
        if (!rawUrl) return null;
        if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
        return `${BASE_URL}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
    };

    const formatFollowUpLabel = (key) =>
        String(key)
            .replace(/([A-Z])/g, ' $1')
            .replace(/[_-]/g, ' ')
            .replace(/^./, (str) => str.toUpperCase());

    const filteredReports = reports.filter((report) =>
        roleFilter === 'all' ? true : (report.role || report.ownerRole) === roleFilter
    );

    if (loading) return <p>Loading reports...</p>;
    if (error) return <p style={{ color: 'red' }}>❌ Error: {error}</p>;
    if (!filteredReports.length) return <p>No reports found.</p>;

    return (
        <div className="agent-report-review">
            <h2 className="title">Unified Report Review</h2>

            {/* Role filter */}
            <div className="role-filter-row">
                <label>
                    <strong>Filter by role:</strong>{' '}
                </label>
                <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                >
                    <option value="all">All</option>
                    <option value="agent">Agents</option>
                    <option value="scout">Scouts</option>
                    <option value="club">Clubs</option>
                </select>
            </div>

            {filteredReports.map((report) => {
                const displayRole = report.role || report.ownerRole || null;
                const displaySubmittedById =
                    report.submittedById || report.ownerId || report.agentId || null;
                const profileLink = getFrontendLink(report.profileUrl);
                const playerProfileLink = getFrontendLink(report.playerProfileLink);

                return (
                    <div className="report-card" key={report.id}>
                        <h3 className="section-title centered">
                            {report.updateType}
                        </h3>

                        <p>
                            <span className="label">Submitted By:</span>{' '}
                            {displayRole
                                ? displayRole.charAt(0).toUpperCase() + displayRole.slice(1)
                                : '—'}{' '}
                            #{displaySubmittedById || '—'}{' '}
                            |{' '}
                            {profileLink ? (
                                <a
                                    href={profileLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link"
                                >
                                    View Profile
                                </a>
                            ) : (
                                '—'
                            )}
                        </p>

                        <p>
                            <span className="label">Summary:</span>{' '}
                            {report.generalExplanation}
                        </p>

                        {playerProfileLink && (
                            <p>
                                <span className="label">Player Profile:</span>{' '}
                                <a
                                    href={playerProfileLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link"
                                >
                                    View Player Profile
                                </a>
                            </p>
                        )}

                        {report.followUps && Object.keys(report.followUps).length > 0 && (
                            <div>
                                <span className="label">Follow-up Details:</span>
                                <ul>
                                    {Object.entries(report.followUps).map(([key, value]) => (
                                        <li key={key}>
                                            <span className="label">{formatFollowUpLabel(key)}:</span>{' '}
                                            {String(value)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {report.fileUrls && report.fileUrls.length > 0 && (
                            <div>
                                <span className="label">Supporting Documents:</span>
                                <ul>
                                    {report.fileUrls.map((url, idx) => (
                                        <li key={idx}>
                                            <a
                                                href={getFileUrl(url)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="link"
                                            >
                                                {url.split('/').pop()}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <p>
                            <em>
                                Submitted on:{' '}
                                {new Date(report.createdAt).toLocaleString()}
                            </em>
                        </p>
                    </div>
                );
            })}
        </div>
    );
};

export default AdminRoleReportReview;
